import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Dialog,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Table,
  TableContainer,
  Tabs,
  Tab,
  Button,
  Typography,
  Stack,
  Container,
  TextField,
} from "@mui/material";
import { getDocs, collection } from "firebase/firestore";
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import { UserListHead } from "../sections/@dashboard/user";
import AddPennant from "./AddPennant";
import AddMembersToPennant from "./AddMembersToPennant";
import PennantMoreMenu from "../sections/@dashboard/user/PennantMoreMenu";
import PennantSettings from "./PennantSettings";
import Iconify from "../components/Iconify";
import Draws from "../draws/Draws";
import Ladder from "./Ladder";
import PendingRequests from "./PendingRequests";
import { db } from "../firebase";

// Updated TABLE_HEAD now includes the description column
const TABLE_HEAD = [
  { id: "participantType", label: "Participant Type", alignRight: false },
  { id: "description", label: "Description", alignRight: false },
  { id: "members", label: "Member Count", alignRight: false },
  { id: "matchFormat", label: "Format", alignRight: false },
  { id: "courtsRequired", label: "Courts Required", alignRight: false },
  { id: "" },
];

function extractYear(pennant) {
  const match = (pennant.seasonName || "").match(/\b(\d{4})\b/);
  return match ? match[1] : "UnknownYear";
}

function parseTimeToMinutes(str) {
  if (!str) return 99999;

  if (/^\d{1,2}:\d{2}$/.test(str)) {
    const [hh, mm] = str.split(":").map(Number);
    return hh * 60 + mm;
  }

  const match = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let hour = parseInt(match[1], 10);
    const mins = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && hour < 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return hour * 60 + mins;
  }

  return 99999;
}

export default function Pennants() {
  const [currentView, setCurrentView] = useState("pennants");
  const [pennantsBySeason, setPennantsBySeason] = useState({});

  const [dataByYear, setDataByYear] = useState({});
  const [allYears, setAllYears] = useState([]);

  const [currentYear, setCurrentYear] = useState("");
  const [allSeasons, setAllSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState("");
  const [allDays, setAllDays] = useState([]);
  const [currentDay, setCurrentDay] = useState("");

  // Controls for AddPennant dialog
  const [open, setOpen] = useState(false);
  const [editPennant, setEditPennant] = useState(null);

  // Controls for PennantSettings dialog
  const [openSettings, setOpenSettings] = useState(false);

  // Controls for AddMembersToPennant
  const [openMember, setOpenMember] = useState(false);

  // For going to Draws or Ladder, or showing pending requests
  const [selectedPennant, setSelectedPennant] = useState(null);

  // For printing all scoresheets by date
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    const fetchPennants = async () => {
      try {
        const snap = await getDocs(collection(db, "Pennants"));
        if (snap.empty) {
          console.warn("No Pennants found in Firestore.");
          return;
        }
        const rawList = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const newDataByYear = {};
        const yearSet = new Set();

        rawList.forEach((p) => {
          const yr = extractYear(p);
          yearSet.add(yr);
          const season = p.seasonName || "UnknownSeason";
          if (!newDataByYear[yr]) newDataByYear[yr] = {};
          if (!newDataByYear[yr][season]) newDataByYear[yr][season] = [];
          newDataByYear[yr][season].push(p);
        });

        const yearArray = [...yearSet].sort();
        setDataByYear(newDataByYear);
        setAllYears(yearArray);

        const nowYear = new Date().getFullYear().toString();
        const defaultYear = yearArray.includes(nowYear) ? nowYear : yearArray[0] || "";
        setCurrentYear(defaultYear);
      } catch (err) {
        console.error("Error loading Pennants from Firestore:", err);
      }
    };
    fetchPennants();
  }, [open, openMember, openSettings]);

  useEffect(() => {
    if (!currentYear || !dataByYear[currentYear]) {
      setAllSeasons([]);
      setCurrentSeason("");
      return;
    }
    const seasonNames = Object.keys(dataByYear[currentYear]).sort();
    setAllSeasons(seasonNames);
    setCurrentSeason(seasonNames[0] || "");
  }, [currentYear, dataByYear]);

  useEffect(() => {
    if (!currentSeason || !dataByYear[currentYear]?.[currentSeason]) {
      setAllDays([]);
      setCurrentDay("");
      return;
    }
    const relevantPennants = dataByYear[currentYear][currentSeason];

    const daySet = new Set();
    relevantPennants.forEach((p) => {
      if (p.pennantDay) daySet.add(p.pennantDay);
    });
    const weekOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const dayArray = Array.from(daySet).sort((a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b));

    setAllDays(dayArray);
    setCurrentDay(dayArray[0] || "");
  }, [currentSeason, currentYear, dataByYear]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------
  const handleYearChange = (event, newYear) => {
    setCurrentYear(newYear);
  };
  const handleSeasonChange = (event, newSeason) => {
    setCurrentSeason(newSeason);
  };
  const handleDayChange = (event, newDay) => {
    setCurrentDay(newDay);
  };

  const handleRowClick = (pennant, evt) => {
    if (evt.defaultPrevented) return;
    setSelectedPennant({ id: pennant.id, seasonId: pennant.seasonId });
    setCurrentView("draw");
  };

  const handleBackToPennants = () => {
    setCurrentView("pennants");
    setSelectedPennant(null);
  };

  const handleNewPennant = () => {
    setOpen(true);
    setEditPennant(null);
  };

  const handleEditPennant = (p) => {
    setEditPennant(p);
    setOpen(true);
  };

  const handleAddMember = (p) => {
    setEditPennant(p);
    setOpenMember(true);
  };

  const handleMenuClick = (event) => event.stopPropagation();

  const handleManageSettings = () => {
    setOpenSettings(true);
  };

  const handleViewPendingRequests = () => {
    setCurrentView("pendingRequests");
    setSelectedPennant(null);
  };

  const handleViewLadder = () => {
    setCurrentView("ladder");
    setSelectedPennant(null);
  };

  const refreshPennants = async () => {
    try {
      const pennantsSnapshot = await getDocs(collection(db, "Pennants"));
      const pennantList = pennantsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const groupedBySeason = {};
      const daySet = new Set();

      pennantList.forEach((p) => {
        const season = p.seasonName || p.seasonId || "Unknown";
        if (!groupedBySeason[season]) {
          groupedBySeason[season] = [];
        }
        groupedBySeason[season].push(p);

        if (p.pennantDay) {
          daySet.add(p.pennantDay);
        }
      });

      setPennantsBySeason(groupedBySeason);
    } catch (error) {
      console.error("Error refreshing pennants: ", error);
    }
  };

  const handlePrintAllForDate = async () => {
    if (!selectedDate) {
      alert("Please select a date.");
      return;
    }
    try {
      const selectedDateObj = new Date(selectedDate);
      const normalizedSelectedDate = selectedDateObj
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "/");

      const pennantsSnapshot = await getDocs(collection(db, "Pennants"));
      if (pennantsSnapshot.empty) {
        alert("No pennants found.");
        return;
      }
      const drawFetchPromises = pennantsSnapshot.docs.map(async (pennantDoc) => {
        const pennantId = pennantDoc.id;
        const pennantData = pennantDoc.data();

        const drawsSnap = await getDocs(collection(db, "Pennants", pennantId, "draws"));
        if (drawsSnap.empty) return [];

        const matchedWeeks = drawsSnap.docs.flatMap((drawDoc) => {
          const { weeks } = drawDoc.data() || {};
          if (!Array.isArray(weeks)) return [];

          return weeks
            .filter((w) => w.date === normalizedSelectedDate)
            .map((w) => ({
              pennantId,
              pennantName: pennantData.name || "Unnamed Pennant",
              weekData: w,
            }));
        });
        return matchedWeeks;
      });

      const arrayOfArrays = await Promise.all(drawFetchPromises);
      const allMatches = arrayOfArrays.flat();
      if (!allMatches.length) {
        alert("No draws found for the selected date.");
        return;
      }

      const paramStr = JSON.stringify(allMatches);
      const qs = new URLSearchParams({ allWeeks: paramStr });
      const url = `/bulk-printable-scoresheets?${qs.toString()}`;

      window.open(url, "_blank");
    } catch (error) {
      console.error("Error fetching draws for the date:", error);
    }
  };

  // ---------------------------------------------------------------------------
  // Helper to render the table of Pennants (grouped by sport and name)
  // ---------------------------------------------------------------------------
  const renderFilteredPennantsTable = () => {
    if (!currentYear || !dataByYear[currentYear]) return null;
    if (!dataByYear[currentYear][currentSeason]) return null;

    const allPennants = dataByYear[currentYear][currentSeason];
    const filtered = allPennants.filter((p) => p.pennantDay === currentDay);

    if (!filtered.length) {
      return <Typography sx={{ mt: 2 }}>No Pennants for this day</Typography>;
    }

    filtered.sort((a, b) => parseTimeToMinutes(a.startTime) - parseTimeToMinutes(b.startTime));

    const bySportAndName = filtered.reduce((acc, row) => {
      // Combine sport and name for grouping; if sport exists, prepend it to the pennant's name.
      const combinedName = row.sport
        ? `${row.sport} ${row.name || "Unnamed"}`
        : row.name || "Unnamed";
      if (!acc[combinedName]) acc[combinedName] = [];
      acc[combinedName].push(row);
      return acc;
    }, {});

    return Object.entries(bySportAndName).map(([displayName, items]) => (
      <Box key={displayName} sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 2 }}>
          {displayName}
        </Typography>
        <Card sx={{ mt: 1 }}>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead order="asc" headLabel={TABLE_HEAD} rowCount={items.length} />
                <TableBody>
                  {items.map((row) => {
                    const totalMembers = Object.values(row.members || {}).reduce(
                      (sum, arr) => sum + (arr?.length || 0),
                      0
                    );
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={(evt) => handleRowClick(row, evt)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox />
                        </TableCell>
                        <TableCell align="left">
                          {row.participantType || "No Participant Type"}
                        </TableCell>
                        {/* New Description Column */}
                        <TableCell align="left">
                          {row.description || "No Description"}
                        </TableCell>
                        <TableCell align="left">{totalMembers}</TableCell>
                        <TableCell align="left">
                          {row.scoringType?.matchFormat || "N/A"}{" "}
                          {row.scoringType?.gamesPerSet || "?"} to{" "}
                          {row.scoringType?.pointsPerGame || "?"}
                        </TableCell>
                        <TableCell align="left">{row.courtsRequired || "N/A"}</TableCell>
                        <TableCell align="right">
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={handleMenuClick}
                            onKeyDown={(ev) => {
                              if (ev.key === "Enter" || ev.key === " ") {
                                ev.stopPropagation();
                              }
                            }}
                          >
                            <PennantMoreMenu
                              pennant={row}
                              onEdit={handleEditPennant}
                              onAddMember={handleAddMember}
                              refreshPennants={refreshPennants}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Box>
    ));
  };

  // ---------------------------------------------------------------------------
  // Functions to render sub-views
  // ---------------------------------------------------------------------------
  const renderPennantsView = () => (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" gutterBottom>
          Pennants
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleNewPennant}
            sx={{ mr: 1 }}
          >
            New Pennant
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="mdi:trophy-outline" />}
            onClick={handleViewPendingRequests}
            sx={{ mr: 1 }}
          >
            View Pending Requests
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="mdi:trophy-outline" />}
            onClick={handleViewLadder}
            sx={{ mr: 1 }}
          >
            View Ladder
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Iconify icon="eva:settings-outline" />}
            onClick={handleManageSettings}
          >
            Manage Pennant Settings
          </Button>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <TextField
          type="date"
          label="Select Date"
          InputLabelProps={{ shrink: true }}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <Button variant="contained" color="primary" onClick={handlePrintAllForDate}>
          Print All Score Sheets
        </Button>
      </Stack>

      {allYears.length > 0 && (
        <Tabs value={currentYear} onChange={handleYearChange} sx={{ mb: 2 }}>
          {allYears.map((yr) => (
            <Tab key={yr} label={yr} value={yr} />
          ))}
        </Tabs>
      )}

      {allSeasons.length > 0 && (
        <Tabs value={currentSeason} onChange={handleSeasonChange} sx={{ mb: 2 }}>
          {allSeasons.map((sn) => (
            <Tab key={sn} label={sn} value={sn} />
          ))}
        </Tabs>
      )}

      {allDays.length > 0 && (
        <Tabs value={currentDay} onChange={handleDayChange} sx={{ mb: 2 }}>
          {allDays.map((d) => (
            <Tab key={d} label={d} value={d} />
          ))}
        </Tabs>
      )}

      {renderFilteredPennantsTable()}
    </>
  );

  const renderDrawView = () =>
    selectedPennant && (
      <Box>
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:arrow-back-fill" />}
          onClick={handleBackToPennants}
          sx={{ mb: 3 }}
        >
          Back to Pennants
        </Button>
        <Draws pennantId={selectedPennant.id} seasonId={selectedPennant.seasonId} />
      </Box>
    );

  const renderPendingRequestsView = () => (
    <Box>
      <Button
        variant="contained"
        startIcon={<Iconify icon="eva:arrow-back-fill" />}
        onClick={handleBackToPennants}
        sx={{ mb: 3 }}
      >
        Back to Pennants
      </Button>
      <PendingRequests />
    </Box>
  );

  const renderLadderView = () => (
    <Box>
      <Button
        variant="contained"
        startIcon={<Iconify icon="eva:arrow-back-fill" />}
        onClick={handleBackToPennants}
        sx={{ mb: 3 }}
      >
        Back to Pennants
      </Button>
      <Ladder />
    </Box>
  );

  function renderView() {
    switch (currentView) {
      case "pennants":
        return renderPennantsView();
      case "draw":
        return renderDrawView();
      case "pendingRequests":
        return renderPendingRequestsView();
      case "ladder":
        return renderLadderView();
      default:
        return renderPennantsView();
    }
  }

  // ---------------------------------------------------------------------------
  // Final Render
  // ---------------------------------------------------------------------------
  return (
    <Page className="no-print" title="Pennants | Ballarat Squash and Racquetball">
      <Container className="no-print">
        {renderView()}

        {/* Single AddPennant dialog for New and Edit */}
        {open && (
          <AddPennant
            isOpen={open}
            toggle={() => setOpen(false)}
            edit={!!editPennant}
            pennantId={editPennant?.id}
          />
        )}

        {/* Dialog for adding members */}
        {openMember && (
          <AddMembersToPennant
            isOpen={openMember}
            toggle={() => setOpenMember(false)}
            pennant={editPennant}
            refreshPennants={refreshPennants}
          />
        )}

        {/* Dialog for pennant settings */}
        {openSettings && (
          <Dialog
            open={openSettings}
            onClose={() => setOpenSettings(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              style: {
                minHeight: "90vh",
                maxHeight: "90vh",
              },
            }}
          >
            <PennantSettings />
          </Dialog>
        )}
      </Container>
    </Page>
  );
}
